import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { formatDateTime } from "../lib/format";
import { toast } from "sonner";
import { MailCheck, Search, X, PartyPopper, Truck, CheckCircle2, XCircle, RefreshCw, Loader2, Clock } from "lucide-react";

const KIND = { nuovo: "Nuova richiesta", sollecito: "Sollecito", reinvio: "Reinvio", rinvio: "Rinvio ad altri partner" };

export const EsitoBadge = ({ ok, error, attempts = 1, stato }) => (
  <span className="inline-flex flex-col items-start gap-0.5">
    {stato === "queued"
      ? <span data-testid="email-esito-queued" title={error || ""} className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold"><Clock className="h-3 w-3" /> In coda</span>
      : ok
      ? <span data-testid="email-esito-ok" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold"><CheckCircle2 className="h-3 w-3" /> Inviata</span>
      : <span data-testid="email-esito-ko" title={error || ""} className="inline-flex items-center gap-1 rounded-full bg-[#942F30]/10 text-[#942F30] border border-[#942F30]/30 px-2 py-0.5 text-[11px] font-semibold"><XCircle className="h-3 w-3" /> Fallita</span>}
    {attempts > 1 && <span data-testid="email-attempts" className="text-[10px] text-amber-700 font-medium">{stato === "queued" ? `${attempts} tentativi finora` : ok ? `al ${attempts}° tentativo` : `dopo ${attempts} tentativi`}</span>}
  </span>
);

export const resendEmails = async (requestId, partnerIds = []) => {
  const { data } = await api.post(`/requests/${requestId}/email-log/resend`, { partner_ids: partnerIds });
  return data;
};

export default function EmailLogPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [esito, setEsito] = useState("all");
  const [kind, setKind] = useState("all");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (esito !== "all") params.esito = esito;
    if (kind !== "all") params.kind = kind;
    if (code.trim()) params.code = code.trim();
    const res = await api.get("/email-log", { params });
    setData(res.data);
  }, [esito, kind, code]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const resend = async (item) => {
    setBusy(item.id);
    try { await resendEmails(item.request_id, [item.partner_id]); toast.success(`Reinvio a ${item.partner_name} avviato`, { description: "Il nuovo esito comparirà tra pochi secondi." }); setTimeout(load, 4000); }
    catch (err) { toast.error("Reinvio non riuscito", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setBusy(null); }
  };
  const hasFilter = esito !== "all" || kind !== "all" || code;

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto nx-fade-in pb-10">
        <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2"><MailCheck className="h-7 w-7 text-primary" /> Registro Invii Email</h1>
        <p className="text-sm text-slate-500 mb-6">Email inviate ai partner per nuovi trasporti ed eventi, con esito e motivo in caso di errore.
          {data?.failed_total > 0 && <span data-testid="email-log-failed-total" className="ml-2 font-semibold text-[#942F30]">· {data.failed_total} invii falliti in totale</span>}</p>

        <Card className="border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
            <Select value={esito} onValueChange={setEsito}>
              <SelectTrigger data-testid="email-filter-esito" className="h-9 w-44"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti gli esiti</SelectItem><SelectItem value="ok">Solo riuscite</SelectItem><SelectItem value="ko">Solo fallite</SelectItem><SelectItem value="coda">Solo in coda</SelectItem></SelectContent>
            </Select>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="email-filter-kind" className="h-9 w-44"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti i tipi</SelectItem>{Object.entries(KIND).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input data-testid="email-filter-code" placeholder="Codice corsa..." value={code} onChange={(e) => setCode(e.target.value)} className="pl-8 h-9 w-44" />
            </div>
            {hasFilter && <Button data-testid="email-clear-filters" variant="ghost" size="sm" className="gap-1 h-8" onClick={() => { setEsito("all"); setKind("all"); setCode(""); }}><X className="h-4 w-4" /> Reset</Button>}
            <Button variant="outline" size="sm" className="gap-1 h-8 ml-auto" onClick={load} data-testid="email-log-refresh"><RefreshCw className="h-3.5 w-3.5" /> Aggiorna</Button>
          </div>
          {!data ? <div className="p-5 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            : data.items.length === 0 ? <p data-testid="email-log-empty" className="py-16 text-center text-sm text-slate-500">Nessun invio registrato.</p>
            : (
              <Table>
                <TableHeader><TableRow><TableHead className="w-36">Data / Ora</TableHead><TableHead className="w-32">Codice</TableHead><TableHead>Partner</TableHead><TableHead className="w-32">Tipo</TableHead><TableHead className="w-28">Esito</TableHead><TableHead>Motivo errore</TableHead><TableHead className="w-24" /></TableRow></TableHeader>
                <TableBody>
                  {data.items.map((it) => (
                    <TableRow key={it.id} data-testid={`email-log-row-${it.id}`} className={it.ok || it.stato === "queued" ? "" : "bg-[#942F30]/[0.03]"}>
                      <TableCell className="text-xs text-slate-600 whitespace-nowrap">{formatDateTime(it.at)}</TableCell>
                      <TableCell>
                        <button type="button" data-testid={`email-log-code-${it.id}`} onClick={() => navigate(`/operativa/richiesta/${it.request_id}`)} className="font-mono text-xs font-semibold text-primary hover:underline flex items-center gap-1">
                          {it.request_type === "evento" ? <PartyPopper className="h-3 w-3" /> : <Truck className="h-3 w-3" />} {it.code}
                        </button>
                      </TableCell>
                      <TableCell className="text-sm"><span className="font-medium text-slate-800">{it.partner_name}</span><span className="block text-xs text-slate-400">{it.partner_email}</span></TableCell>
                      <TableCell className="text-xs text-slate-600">{KIND[it.kind] || it.kind}</TableCell>
                      <TableCell><EsitoBadge ok={it.ok} error={it.error} attempts={it.attempts} stato={it.stato} /></TableCell>
                      <TableCell className="text-xs text-[#942F30] break-words max-w-xs" data-testid={`email-log-error-${it.id}`}>{it.ok ? "—" : it.stato === "queued" ? <span className="text-amber-700">{it.error || "In attesa di invio"}</span> : it.error}</TableCell>
                      <TableCell className="text-right">
                        {!it.ok && it.stato !== "queued" && <Button data-testid={`email-log-resend-${it.id}`} size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={busy === it.id} onClick={() => resend(it)}>{busy === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Rinvia</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </Card>
      </div>
    </AppLayout>
  );
}
